// apps/web/src/lib/estoque/parametros.ts — Fase 13, Plano 09 (EST-08, EST-12, D-03, D-10, D-21).
// Validação PURA dos parâmetros de estoque/compra do material, com as mesmas regras e mensagens do servidor
// (FATOR_INVALIDO, FATOR_OBRIGATORIO, ALTO_GIRO_SEM_TINTA). O servidor revalida: isto só dá resposta imediata na tela.
import { interpretarNumeroPtBr, mensagemNumeroAmbiguo, normalizarUnidade } from '@pieltec/contracts';

export const MENSAGEM_FATOR_MAIOR_QUE_ZERO = 'O fator de conversão deve ser maior que zero.';
export const MENSAGEM_FATOR_OBRIGATORIO = 'Informe o fator: a unidade de compra é diferente da unidade de estoque.';
export const MENSAGEM_FATOR_ILEGIVEL = 'Informe o fator de conversão como número. Use vírgula para decimais, sem ponto de milhar.';
export const MENSAGEM_ALTO_GIRO_SEM_TINTA = 'Alto giro, sem devolução só vale para materiais com tipo de tinta.';

/** D-22 / UI-27: mensagens do campo Estoque mínimo (mesmo texto do servidor para "maior que zero", com a dica de deixar em branco). */
export const MENSAGEM_ESTOQUE_MINIMO_MAIOR_QUE_ZERO = 'O estoque mínimo deve ser maior que zero. Deixe em branco para não definir.';
export const MENSAGEM_ESTOQUE_MINIMO_ILEGIVEL = 'Use apenas números, com vírgula ou ponto decimal.';
export const MENSAGEM_ESTOQUE_MINIMO_CASAS = 'Use no máximo quatro casas decimais.';
export const MENSAGEM_ESTOQUE_MINIMO_MAXIMO = 'O estoque mínimo deve ser no máximo 99.999.999.999,9999.';

/** Maior estoque mínimo que cabe no servidor (NUMERIC(15,4)); mesmo limite do DTO da API (WR-01). */
export const ESTOQUE_MINIMO_MAXIMO = 99_999_999_999.9999;

export type FatorDigitado =
  | { readonly tipo: 'vazio' }
  | { readonly tipo: 'ok'; readonly valor: number }
  | { readonly tipo: 'erro'; readonly mensagem: string };

/** Lê o fator digitado em pt-BR. "2.500" (ambíguo) é erro: nunca se adivinha o milhar. Não positivo é erro. */
export function lerFatorDigitado(texto: string): FatorDigitado {
  const resultado = interpretarNumeroPtBr(texto);
  switch (resultado.tipo) {
    case 'vazio':
      return { tipo: 'vazio' };
    case 'invalido':
      return { tipo: 'erro', mensagem: MENSAGEM_FATOR_ILEGIVEL };
    case 'ambiguo':
      return { tipo: 'erro', mensagem: mensagemNumeroAmbiguo(texto, resultado) };
    case 'ok':
      return resultado.valor > 0 ? { tipo: 'ok', valor: resultado.valor } : { tipo: 'erro', mensagem: MENSAGEM_FATOR_MAIOR_QUE_ZERO };
  }
}

export type EstoqueMinimoDigitado =
  | { readonly tipo: 'vazio' }
  | { readonly tipo: 'ok'; readonly valor: number }
  | { readonly tipo: 'invalido'; readonly mensagem: string };

const NUMERO_SIMPLES = /^(-?)(\d+)(?:[.,](\d+))?$/;

/**
 * Lê o estoque mínimo digitado (D-22): vazio = sem mínimo; vírgula ou ponto como separador decimal, sem separador de
 * milhar ("1.000,5" é recusado, nunca se adivinha), até 4 casas e maior que zero.
 */
export function lerEstoqueMinimoDigitado(texto: string): EstoqueMinimoDigitado {
  const limpo = texto.trim();
  if (limpo === '') return { tipo: 'vazio' };
  const partes = NUMERO_SIMPLES.exec(limpo);
  if (!partes) return { tipo: 'invalido', mensagem: MENSAGEM_ESTOQUE_MINIMO_ILEGIVEL };
  const [, sinal, inteiro, decimais = ''] = partes;
  const valor = Number(`${sinal}${inteiro}.${decimais === '' ? '0' : decimais}`);
  if (valor <= 0) return { tipo: 'invalido', mensagem: MENSAGEM_ESTOQUE_MINIMO_MAIOR_QUE_ZERO };
  if (decimais.length > 4) return { tipo: 'invalido', mensagem: MENSAGEM_ESTOQUE_MINIMO_CASAS };
  if (valor > ESTOQUE_MINIMO_MAXIMO) {
    return { tipo: 'invalido', mensagem: MENSAGEM_ESTOQUE_MINIMO_MAXIMO };
  }
  return { tipo: 'ok', valor };
}

/** Estoque mínimo vindo do servidor para o campo digitado (vírgula decimal; vazio quando não há mínimo). */
export function estoqueMinimoParaCampo(valor: number | null): string {
  return valor === null ? '' : String(valor).replace('.', ',');
}

/** Unidade de compra efetiva: vazia = a própria unidade de estoque. */
export function unidadeCompraEfetiva(unidadeEstoque: string, unidadeCompra: string): string {
  return unidadeCompra.trim() === '' ? unidadeEstoque : unidadeCompra;
}

export function unidadesIguais(unidadeEstoque: string, unidadeCompra: string): boolean {
  return normalizarUnidade(unidadeEstoque) === normalizarUnidade(unidadeCompraEfetiva(unidadeEstoque, unidadeCompra));
}

/** Unidades iguais -> fator 1; senão o fator informado (null quando ainda não há). */
export function fatorEfetivo(unidadeEstoque: string, unidadeCompra: string, fator: number | null): number | null {
  return unidadesIguais(unidadeEstoque, unidadeCompra) ? 1 : fator;
}

/** "Quantas {unidade de estoque} cabem em 1 {unidade de compra}. Ex.: caixa = 20 kg." */
export function textoAjudaFator(unidadeEstoque: string, unidadeCompra: string): string {
  const estoque = unidadeEstoque.trim() === '' ? 'unidades de estoque' : unidadeEstoque.trim();
  const compra = unidadeCompra.trim() === '' ? 'unidade de compra' : unidadeCompra.trim();
  return `Quantas ${estoque} cabem em 1 ${compra}. Ex.: caixa = 20 kg.`;
}

export interface EntradaParametrosEstoque {
  readonly unidadeEstoque: string;
  readonly unidadeCompra: string;
  /** Fator como digitado (pt-BR). */
  readonly fator: string;
  readonly tipoTinta: string | null;
  readonly altoGiro: boolean;
}

/** Mensagens de validação do UI-SPEC (lista vazia = válido). */
export function validarParametrosEstoque(entrada: EntradaParametrosEstoque): string[] {
  const mensagens: string[] = [];
  if (!unidadesIguais(entrada.unidadeEstoque, entrada.unidadeCompra)) {
    const fator = lerFatorDigitado(entrada.fator);
    if (fator.tipo === 'vazio') mensagens.push(MENSAGEM_FATOR_OBRIGATORIO);
    else if (fator.tipo === 'erro') mensagens.push(fator.mensagem);
  }
  if (entrada.altoGiro && !entrada.tipoTinta) mensagens.push(MENSAGEM_ALTO_GIRO_SEM_TINTA);
  return mensagens;
}

/** Código de negócio da API -> mensagem pt-BR. null quando o código não é de parâmetros de estoque. */
export function mensagemErroParametros(codigo: string | null | undefined): string | null {
  switch (codigo) {
    case 'FATOR_INVALIDO':
      return MENSAGEM_FATOR_MAIOR_QUE_ZERO;
    case 'FATOR_OBRIGATORIO':
      return MENSAGEM_FATOR_OBRIGATORIO;
    case 'ALTO_GIRO_SEM_TINTA':
      return MENSAGEM_ALTO_GIRO_SEM_TINTA;
    case 'PARAMETROS_VERSAO_DESATUALIZADA':
      return 'Os parâmetros de estoque foram alterados por outro usuário. Recarregue para ver as mudanças antes de salvar.';
    case 'ESTOQUE_MINIMO_INVALIDO':
      return MENSAGEM_ESTOQUE_MINIMO_MAIOR_QUE_ZERO;
    case 'ESTOQUE_MINIMO_FORA_DO_LIMITE':
      return MENSAGEM_ESTOQUE_MINIMO_MAXIMO;
    case 'LOCALIZACAO_INVALIDA':
      return 'O estoque preferencial precisa ser uma localização ativa.';
    default:
      return null;
  }
}

/** Fator vindo do servidor para o campo digitado (vírgula decimal, sem milhar). */
export function fatorParaCampo(fator: number | null): string {
  return fator === null ? '' : String(fator).replace('.', ',');
}

/** Campos da edição em massa de "Estoque e compras" (o mesmo recorte de `ParametrosEmMassaInput['campos']`). */
export interface CamposEstoqueEmMassa {
  readonly unidadeCompra?: { readonly valor: string | null };
  readonly fatorConversao?: { readonly valor: number | null };
  readonly estoquePreferencialId?: { readonly valor: string | null };
  readonly altoGiroSemDevolucao?: { readonly valor: boolean };
}

/**
 * Linhas "campo → valor" da confirmação da edição em massa (WR-C08): quem confirma vê exatamente o que será gravado em todos
 * os materiais selecionados. `nomeLocal` traduz o id do estoque preferencial para o nome mostrado na tela.
 */
export function linhasResumoEmMassa(campos: CamposEstoqueEmMassa, nomeLocal: (id: string) => string | null): string[] {
  const linhas: string[] = [];
  if (campos.unidadeCompra) linhas.push(`Unidade de compra → ${campos.unidadeCompra.valor ?? 'sem unidade de compra'}`);
  if (campos.fatorConversao) {
    const fator = campos.fatorConversao.valor;
    linhas.push(`Fator de conversão → ${fator === null ? 'sem fator' : fatorParaCampo(fator)}`);
  }
  if (campos.estoquePreferencialId) {
    const id = campos.estoquePreferencialId.valor;
    linhas.push(`Estoque preferencial → ${id === null ? 'nenhum' : (nomeLocal(id) ?? 'local escolhido')}`);
  }
  if (campos.altoGiroSemDevolucao) linhas.push(`Alto giro sem devolução → ${campos.altoGiroSemDevolucao.valor ? 'sim' : 'não'}`);
  return linhas;
}
